import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Card, Select, Row, Col, Spin, Space, Tag, Progress, Empty,
} from "antd";
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
    SafetyCertificateOutlined, CheckCircleOutlined,
    ExclamationCircleOutlined, CloseCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const fmtCurrency = (v) =>
    `INR ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const COMPLIANCE_DEFS = [
    { key: "pf",  label: "Provident Fund (PF)",   abbr: "PF",  color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
    { key: "esi", label: "Employee State Ins (ESI)", abbr: "ESI", color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
    { key: "pt",  label: "Professional Tax (PT)",  abbr: "PT",  color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
    { key: "tds", label: "Tax Deducted at Source (TDS)", abbr: "TDS", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
];

function StatusBadge({ value }) {
    if (!value && value !== 0) return <Tag style={{ borderRadius: 8, fontWeight: 700 }}>No Data</Tag>;
    if (value > 0) return (
        <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 8, fontWeight: 700 }}>
            Filed
        </Tag>
    );
    return (
        <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ borderRadius: 8, fontWeight: 700 }}>
            Pending
        </Tag>
    );
}

export default function StatutoryCompliance() {
    const { authData, backUrl } = useContext(AuthContext);
    const [rawData, setRawData] = useState({});
    const [chartData, setChartData] = useState([]);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisation, setOrganisation] = useState("ALL");
    const [organisations, setOrganisations] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisation !== "ALL") params.organisation_id = organisation;

        axios.get(`${backUrl}/api/reports/statutory-compliance/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            const apiData = res.data || {};
            setRawData(apiData);

            const built = Object.keys(apiData)
                .filter(k => !["month", "year", "organisation_id"].includes(k))
                .map(k => ({
                    name: k.replace(/_/g, " ").toUpperCase(),
                    value: Number(apiData[k]) || 0,
                }))
                .filter(item => item.value > 0);

            setChartData(built.length > 0 ? built : []);
        }).catch(() => setChartData([]))
          .finally(() => setLoading(false));
    }, [organisation, month, year, backUrl, authData?.access]);

    const PIE_COLORS = ["#6366f1", "#3b82f6", "#0891b2", "#f59e0b", "#ef4444", "#10b981"];

    const totalFiled = COMPLIANCE_DEFS.filter(d => rawData[d.key] > 0).length;
    const totalDefs = COMPLIANCE_DEFS.length;

    return (
        <Card
            variant="borderless"
            style={{
                borderRadius: 16,
                background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                border: "1px solid #e2e8f0",
            }}
            styles={{ body: { padding: "20px 22px" } }}
        >
            {/* Header */}
            <Row justify="space-between" align="middle" gutter={[0, 12]} style={{ marginBottom: 18 }}>
                <Col xs={24} md={12}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 11,
                            background: "linear-gradient(135deg, #d97706, #f59e0b)",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                            <SafetyCertificateOutlined style={{ color: "#fff", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                Statutory Compliance Summary
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                PF · ESI · PT · TDS — {MONTHS[month - 1]} {year}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <Space wrap size={6} style={{ justifyContent: "flex-end", width: "100%" }}>
                        <Select
                            value={organisation}
                            onChange={(v) => setOrganisation(v || "ALL")}
                            allowClear
                            onClear={() => setOrganisation("ALL")}
                            style={{ width: 175 }}
                            size="small"
                        >
                            <Option value="ALL">All Organisations ({organisations.length})</Option>
                            {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                        </Select>
                        <Select value={month} onChange={setMonth} style={{ width: 108 }} size="small">
                            {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
                        </Select>
                        <Select value={year} onChange={setYear} style={{ width: 80 }} size="small">
                            {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
                        </Select>
                    </Space>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                        Loading Statutory Compliance...
                    </div>
                </div>
            ) : (
                <>
                    {/* Overall Filing Progress */}
                    <div style={{
                        background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                        border: "1px solid #bbf7d0", borderRadius: 12,
                        padding: "12px 16px", marginBottom: 18,
                        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
                    }}>
                        <div>
                            <div style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>
                                Compliance Status — {MONTHS[month - 1]} {year}
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                                {totalFiled} of {totalDefs} statutory items recorded for this period
                            </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Progress
                                type="circle"
                                percent={Math.round((totalFiled / totalDefs) * 100)}
                                size={56}
                                strokeColor={totalFiled === totalDefs ? "#10b981" : totalFiled > 0 ? "#f59e0b" : "#ef4444"}
                                format={p => (
                                    <span style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{p}%</span>
                                )}
                            />
                        </div>
                    </div>

                    {/* Compliance Type Cards */}
                    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                        {COMPLIANCE_DEFS.map(def => {
                            const val = rawData[def.key];
                            const hasCoverage = val && Number(val) > 0;
                            return (
                                <Col xs={12} sm={6} key={def.key}>
                                    <div style={{
                                        background: hasCoverage ? def.bg : "#f8fafc",
                                        border: `1px solid ${hasCoverage ? def.border : "#e2e8f0"}`,
                                        borderRadius: 12, padding: "12px 14px",
                                        textAlign: "center",
                                        transition: "all 0.2s ease",
                                    }}>
                                        <div style={{ marginBottom: 6 }}>
                                            <StatusBadge value={val} />
                                        </div>
                                        <div style={{
                                            fontSize: 22, fontWeight: 900,
                                            color: hasCoverage ? def.color : "#94a3b8",
                                            lineHeight: 1, marginBottom: 4,
                                        }}>
                                            {def.abbr}
                                        </div>
                                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>
                                            {def.label}
                                        </div>
                                        {hasCoverage && (
                                            <div style={{ fontSize: 12, fontWeight: 800, color: def.color }}>
                                                {fmtCurrency(val)}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>

                    {/* Pie chart of raw API data */}
                    {chartData.length > 0 ? (
                        <>
                            <div style={{
                                fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8
                            }}>
                                Compliance Contribution Breakdown
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={3}
                                        label={({ name, percent }) =>
                                            `${name}: ${(percent * 100).toFixed(0)}%`
                                        }
                                        labelLine={false}
                                    >
                                        {chartData.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={v => [fmtCurrency(v)]}
                                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                                    />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </>
                    ) : (
                        <Empty
                            description="No statutory compliance amounts recorded for this period"
                            style={{ padding: "30px 0" }}
                        />
                    )}
                </>
            )}
        </Card>
    );
}
